import { useMemo } from 'react'
import { useIsFetching } from '@tanstack/react-query'
import {
  OwnerId,
  useGetAvdelingerQueries,
  useGetRegionerQuery,
  useGetSeksjonerQueries
} from 'prove-common'
import type { UnitOption } from './teamsUtils'

export type OrganizationData = {
  regioner: ReturnType<typeof useGetRegionerQuery>['data']
  avdelinger: ReturnType<typeof useGetAvdelingerQueries>['data']
  seksjoner: ReturnType<typeof useGetSeksjonerQueries>['data']
  isRegionerError: boolean
  isAvdelingerError: boolean
  isSeksjonerError: boolean
  isOrganizationError: boolean
  isLoadingOrganization: boolean
  organizationFetching: boolean
  seksjonOptions: UnitOption[]
  avdelingOptions: UnitOption[]
  avdelingNameById: Map<string, string>
  seksjonNameById: Map<string, string>
  seksjonAvdelingById: Map<string, string>
}

export function useOrganizationData(): OrganizationData {
  const {
    data: regioner,
    isLoading: isLoadingRegioner,
    isError: isRegionerError
  } = useGetRegionerQuery()
  const {
    data: avdelinger = [],
    isLoading: isLoadingAvdelinger,
    isError: isAvdelingerError
  } = useGetAvdelingerQueries((regioner ?? []).map(({ id }) => id))
  const {
    data: seksjoner = [],
    isLoading: isLoadingSeksjoner,
    isError: isSeksjonerError
  } = useGetSeksjonerQueries(
    avdelinger.map(({ id }) => new OwnerId('avdeling', id))
  )

  const isOrganizationError =
    isRegionerError || isAvdelingerError || isSeksjonerError
  const isLoadingOrganization =
    isLoadingRegioner || isLoadingAvdelinger || isLoadingSeksjoner
  const organizationFetching =
    useIsFetching({
      predicate: (query) =>
        ['regioner', 'avdelinger', 'seksjoner'].includes(
          String(query.queryKey[0])
        )
    }) > 0

  const seksjonOptions = useMemo(
    () =>
      seksjoner
        .map(({ id, navn, avdelingId }) => ({
          value: id,
          label: navn,
          avdelingId
        }))
        .toSorted((a, b) => a.label.localeCompare(b.label, 'nb')),
    [seksjoner]
  )
  const avdelingOptions = useMemo(
    () =>
      avdelinger
        .map(({ id, navn }) => ({ value: id, label: navn }))
        .toSorted((a, b) => a.label.localeCompare(b.label, 'nb')),
    [avdelinger]
  )
  const avdelingNameById = useMemo(
    () => new Map(avdelingOptions.map(({ value, label }) => [value, label])),
    [avdelingOptions]
  )
  const seksjonNameById = useMemo(
    () => new Map(seksjonOptions.map(({ value, label }) => [value, label])),
    [seksjonOptions]
  )
  const seksjonAvdelingById = useMemo(
    () =>
      new Map(
        seksjonOptions.map(({ value, avdelingId }) => [value, avdelingId ?? ''])
      ),
    [seksjonOptions]
  )

  return {
    regioner,
    avdelinger,
    seksjoner,
    isRegionerError,
    isAvdelingerError,
    isSeksjonerError,
    isOrganizationError,
    isLoadingOrganization,
    organizationFetching,
    seksjonOptions,
    avdelingOptions,
    avdelingNameById,
    seksjonNameById,
    seksjonAvdelingById
  }
}
